import {EO, get, IS, ObjectUtils, resolvePolymorphVar} from "@green24/npm-javascript-utils";
import {Reducer} from "./Reducer";
import {STORE_UPDATE_EVENT} from "../../models/constants/StoreConstants";

const REDUCER_IDENTIFIER = "<|REDUCER|>";
const INITIALIZE_ACTION_KEY = "__INIT__";

export const markAsReducer = (reducersStructure) => {
	return ObjectUtils.mapAsObject(reducersStructure, (key, value) => {
		return {
			key: REDUCER_IDENTIFIER + key,
			value,
		}
	});
};

export class Store {
	/** @type {LogGroup} **/
	_logGroup;

	constructor(rootReducer, onUpdate = () => console.error("Store has no onUpdate defined.", this), enableLogger = false, loggerPrefix = '') {
		if(rootReducer === undefined) {
			throw Error(`Root reducer is not defined!`);
		}

		this._reducerStructure = rootReducer;
		this.onUpdateCallback = onUpdate;
		this._loggerEnabled = enableLogger;
		this.loggerPrefix = loggerPrefix;

		this._listeners = [];
		this._state = {};

		this._reducers = this.scanForReducers();
		this._stateChanges = [];

		this.initialize();
	}

	get loggerEnabled() {
		return resolvePolymorphVar(
			this._loggerEnabled,
			{
				function: f => f(),
			},
			() => this._loggerEnabled,
			true
		);
	}

	get rootReducer() {
		return this._reducerStructure;
	}

	initialize() {
		const state = EO();
		this._reducers.forEach(reducer => {
			if(IS.property(reducer.actionHandlers, INITIALIZE_ACTION_KEY) || IS.property(reducer.actionHandlers, '*')) {
				const initFunction = reducer.actionHandlers[INITIALIZE_ACTION_KEY] || reducer.actionHandlers['*'];
				state.set(reducer.fullPath, initFunction(undefined, {type: INITIALIZE_ACTION_KEY}));
			}

			this.registerActionsListenersForReducer(reducer);
		});

		this._state = state;
	}

	/**
	 * @deprecated
	 */
	destroy() {}

	getState() {
		return this._state;
	}

	dispatch(data) {
		this._stateChanges = [];

		if(this.loggerEnabled) {
			this._logGroup = new LogGroup({
				label: `${this.loggerPrefix} Action`,
				labelColor: "#888",
				value: data.type,
			});
		}

		const relevantListeners = this._listeners.filter(listener => listener.type == data.type || listener.type == '*');
		relevantListeners.forEach((listener, i) => {
			listener.callback(data, i == relevantListeners.length - 1);
		});

		if(relevantListeners.length === 0) {
			console.warn(`No reducer found for action:`, data);
		}

		if(this.loggerEnabled) {
			this._logGroup.print();
			this._logGroup = null;
		}
	}

	onUpdate(callback) {
		this.onUpdateCallback = callback;
	}

	scanForReducers(reducerStructure = this.rootReducer) {
		return Store.scanForReducers(reducerStructure);
	}

	static scanForReducers(reducerStructure = {}, path = '') {
		return ObjectUtils.map(reducerStructure, (key, value) => {
			if(IS.fnc(value)) {
				return new Reducer(key, path, {"*": value});
			}

			if(
				key.includes(REDUCER_IDENTIFIER) ||
				(
					IS.object(value) && Object.values(value).every(possibleReducerAction => IS.fnc(possibleReducerAction))
				)
			) {
				return new Reducer(key, path, value);
			}

			return this.scanForReducers(value, path ? path + '.' + key : key);
		}).flat(1);
	}

	registerActionsListenersForReducer(reducer) {
		ObjectUtils.forEach(reducer.actionHandlers, (actionName, handler) => {
			if(![INITIALIZE_ACTION_KEY].includes(actionName)) {
				this._listeners.push({
					type: actionName,
					callback: (action, isLast) => this._handleReducerAction(action, handler, reducer, isLast),
				});
			}
		});
	}

	_handleReducerAction(action, handler, reducer, isLast) {
		let handlerValue = handler(get(this._state, reducer.fullPath), action);
		const state = EO(this._state);
		state.set(reducer.fullPath, handlerValue);

		this._stateChanges.push({
			path: reducer.fullPath,
			value: handlerValue,
		});

		this.setState(
			state,
			{
				action,
				isLast,
			},
		);
	}

	setState(newState, loggerDetails = {}) {
		const oldState = {...this._state};
		//Log previous state and action
		if(this.loggerEnabled && this._stateChanges.length == 1) {
			this._logGroup.addEntry({
				label: `Previous state`,
				labelColor: "#777",
				value: oldState,
			});
			this._logGroup.addEntry({
				label: `Action`,
				labelColor: "cyan",
				value: loggerDetails.action,
			});
		}

		this._state = newState;

		//Log state changes
		if(this.loggerEnabled && loggerDetails.isLast) {
			let group = this._logGroup;
			group.addEntry({
				label: "New state",
				labelColor: "#0f8",
				value: newState,
			});

			if(this._stateChanges.length > 1) {
				group = new LogGroup({
					label: `State changes`,
					labelColor: "pink",
					value: `(${this._stateChanges.length})`,
				});
			}

			this._stateChanges.forEach(change => {
				group.addEntry({
					label: change.path,
					labelColor: this._stateChanges.length > 1 ? "#666" : "pink",
					value: change.value,
				});
			});
			//Group can also be _logGroup which would cause self recursion while printing
			group !== this._logGroup && this._logGroup.addEntry(group);
		}

		this.onUpdateCallback(newState, oldState);

		window.dispatchEvent(new CustomEvent(STORE_UPDATE_EVENT, {
			detail: {
				oldState,
				newState,
			},
		}));
	}
}

class LogGroup {
	label;
	labelColor;
	value;

	entries = [];

	constructor({label, labelColor, value}) {
		this.label = label;
		this.labelColor = labelColor;
		this.value = value;
	}

	addEntry(entry) {
		this.entries.push(entry);
	}

	print() {
		if(this.labelColor) {
			console.groupCollapsed(
				`%c${this.label}`, `color: ${this.labelColor}`,
				this.value,
			);
		}
		else {
			console.groupCollapsed(
				this.label,
				this.value,
			);
		}

		this.entries.forEach(entry => {
			if(entry instanceof LogGroup) {
				entry.print();
			}
			else if(entry.labelColor) {
				console.log(`%c${entry.label}`, `color: ${entry.labelColor}`, entry.value);
			}
			else {
				console.log(entry.label, entry.value);
			}
		});

		if(this.entries.length === 0) {
			console.log("%cNo changes were made.", "color: gray");
		}

		console.groupCollapsed("Trace");
		console.trace();
		console.groupEnd();

		console.groupEnd();
	}
}
